import config from '@/config/config-esm'
import { FormDataState } from '@/store/reducers/formData'
import { AppDataState } from '@/store/reducers/appData'

declare global {
  interface Window {
    getOrderPriceBreakdown?: (appData: AppDataState, formData: FormDataState) => OrderPriceBreakdown
  }
}

const EXTRA_PRICE_PER_ADULT = 210
const EXTRA_PRICE_PER_CHILD = 100

interface PriceRule {
  percentage?: number
  subtract?: number
  target: 'item' | 'order'
}

interface PriceRules {
  discounts: Record<string, PriceRule>
}

interface Campaign {
  id: string
  name?: string
  priceRule?: PriceRule
  [key: string]: any
}

interface ExtraService {
  id: string
  name: string
  amount: number
  [key: string]: any
}

interface Package {
  id: string
  amount: number
  [key: string]: any
}

interface Family {
  selectedPackageId?: string
  digitalSafety?: boolean
  counts: {
    adult: number
    child: number
  }
  members: any[]
  [key: string]: any
}

interface PriceItem {
  description: string
  grossAmount: number
  netAmount: number
  extras: PriceExtra[]
  discounts: PriceDiscount[]
}

interface PriceExtra {
  description: string
  grossAmount: number
  netAmount: number
}

interface PriceDiscount {
  description: string
  grossAmount: number
  netAmount: number
  priceRule?: PriceRule
}

interface SharedPrices {
  extras: PriceExtra[]
  discounts: PriceDiscount[]
}

interface OrderPriceBreakdown {
  description: string
  grossAmount: number
  netAmount: number
  extras: PriceExtra[]
  discounts: PriceDiscount[]
}

const priceRules: PriceRules = {
  discounts: {
    // Under 30
    '9b9ac34b-1867-43aa-8a78-757cca77bed9': {
      percentage: 20,
      target: 'item',
    },
    // Rabatt: Enkeltabonnement 10%
    'e2ef5786-3dda-47e0-982f-7a634fd4b056': {
      percentage: 10,
      target: 'item',
    },
  },
}

const isFamily = (formData: FormDataState): boolean => {
  return Boolean(formData.family && formData.family.members && formData.family.members.length)
}

export const getFamilySubscriptionTotalPrice = (family: Family, pkg: Package): number => {
  return (
    pkg.amount +
    family.counts.adult * EXTRA_PRICE_PER_ADULT +
    family.counts.child * EXTRA_PRICE_PER_CHILD
  )
}

export const getMonthlyPrice = (appData: AppDataState, formData: FormDataState): number => {
  if (isFamily(formData)) {
    console.log('formData.family.selectedPackageId', formData.family.selectedPackageId)
    console.log('appData.familyDataPackages', appData.familyDataPackages)
    const pkg = formData.family.selectedPackageId
      ? appData.familyDataPackages.find((p: Package) => p.id === formData.family.selectedPackageId)
      : null
    if (pkg) {
      const packagePrice = getFamilySubscriptionTotalPrice(formData.family, pkg)
      return packagePrice
    }
    return 0
  } else {
    let sum = 0
    for (const phoneItem of formData.phoneItems || []) {
      if (phoneItem.subscription && phoneItem.subscription.amount) {
        sum += phoneItem.subscription.amount
      }
    }
    for (const powerItem of formData.powerItems || []) {
      if (powerItem.powerPlan && powerItem.powerPlan.amount) {
        sum += powerItem.powerPlan.amount
      }
    }
    return sum
  }
}

export const getOrderPriceBreakdown = (appData: AppDataState, formData: FormDataState): OrderPriceBreakdown => {
  const items = [...(formData.phoneItems || []), ...(formData.powerItems || [])]
  const packageData = getPackage(appData, formData)
  const shared = getShared(appData, formData, items)

  const grossAmount = getGrossTotal(appData, formData, items, packageData, shared)
  const netAmount = grossAmount // Simplified for now

  return {
    description: getPackagePriceDescription(appData, formData, items),
    grossAmount,
    netAmount,
    extras: [...packageData.extras, ...shared.extras],
    discounts: [...packageData.discounts, ...shared.discounts],
  }
}

const getPackage = (appData: AppDataState, formData: FormDataState): PriceItem => {
  if (isFamily(formData)) {
    const pkg = formData.family.selectedPackageId
      ? appData.familyDataPackages.find((p: Package) => p.id === formData.family.selectedPackageId)
      : null
    if (pkg) {
      const packagePrice = getFamilySubscriptionTotalPrice(formData.family, pkg)
      return {
        description: pkg.name || 'Family Package',
        grossAmount: packagePrice,
        netAmount: packagePrice,
        extras: getPackageExtras(appData, formData),
        discounts: getPackageDiscounts(appData, formData),
      }
    }
  }
  return {
    description: 'No package selected',
    grossAmount: 0,
    netAmount: 0,
    extras: [],
    discounts: [],
  }
}

const getShared = (appData: AppDataState, formData: FormDataState, items: any[]): SharedPrices => {
  return {
    extras: getSharedExtras(appData, formData),
    discounts: getSharedDiscounts(appData, formData, items),
  }
}

const getGrossTotal = (appData: AppDataState, formData: FormDataState, items: any[], packageData: PriceItem, shared: SharedPrices): number => {
  let sum = packageData.netAmount || 0
  for (const i of items) {
    sum += i.netAmount || 0
  }
  return sum
}

// UTILITY FUNCTIONS

const getPackageExtras = (appData: AppDataState, formData: FormDataState): PriceExtra[] => {
  if (isFamily(formData) && formData.family.digitalSafety) {
    const extra = appData.extraServices.find((e: ExtraService) => e.id === config.digitalSafetyId)
    if (extra) {
      return [
        {
          description: extra.name,
          grossAmount: extra.amount,
          netAmount: extra.amount,
        },
      ]
    }
  }
  return []
}

const getPackageDiscounts = (appData: AppDataState, formData: FormDataState): PriceDiscount[] => {
  return []
}

const getSharedExtras = (appData: AppDataState, formData: FormDataState): PriceExtra[] => {
  return []
}

const getSharedDiscounts = (appData: AppDataState, formData: FormDataState, items: any[]): PriceDiscount[] => {
  return []
}

const getPackagePriceDescription = (appData: AppDataState, formData: FormDataState, items: any[]): string => {
  if (isFamily(formData)) {
    return 'Family Package'
  }
  return 'Individual Items'
}

const getCampaignsWithPriceRules = (campaigns: string[], appData: AppDataState): Campaign[] => {
  return campaigns.map((c: string) => {
    console.log('c', c)
    const campaign = appData.campaigns.find((cmp: Campaign) => cmp.id === c)
    console.log('campaign', campaign)
    return { ...campaign, priceRule: priceRules.discounts[c] }
  })
}

const subtractDiscountsAndAddExtras = (initialSum: number, discounts: PriceDiscount[], extras: PriceExtra[]): number => {
  let sum = initialSum
  for (const discount of discounts) {
    sum = handlePriceRule(sum, discount.priceRule)
  }
  return sum
}

const handlePriceRule = (sum: number, priceRule?: PriceRule): number => {
  if (priceRule) {
    if (priceRule.subtract) {
      sum -= priceRule.subtract
    }
    if (priceRule.percentage) {
      sum = sum * ((100 - priceRule.percentage) / 100)
    }
  }
  return sum
}

window.getOrderPriceBreakdown = getOrderPriceBreakdown